import axios from "axios";
import Config from "../utils/Config";

class EventService {
  getHeaders() {
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
      },
    };
  }

  async getEvents() {
    try {
      const response = await axios.get(
        `${Config.API_BASE_URL}/admin/events`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message: response.data.data?.reason || "Events fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.reason ||
          "An error occurred while getting events",
      };
    }
  }

  async getEvent(event_slug) {
    try {
      const response = await axios.get(
        `${Config.API_BASE_URL}/admin/events/${event_slug}`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message: response.data.data?.reason || "Event fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.reason ||
          "An error occurred while fetching event",
      };
    }
  }

  async createEvent(payload) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/create/event`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message: response.data?.data?.reason || "Event created successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = "An error occurred while creating event";

      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }

      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
        errors: errorData?.errors || null,
      };
    }
  }

  async updateEvent(event_slug, payload) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/update/event/${event_slug}`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message: response.data?.reason || "Event updated successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = "An error occurred while updating event";

      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
        errors: errorData?.errors || null,
      };
    }
  }

  async deleteEvent(event_slug) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/delete/event/${event_slug}`,
        {},
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message: response.data?.data?.reason || "Event deleted successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = "An error occurred while deleting event";

      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }

      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
        errors: errorData?.errors || null,
      };
    }
  }

  async rescheduleEvent(event_slug, payload) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/reschedule/event/${event_slug}`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.message ||
          response.data?.data?.reason ||
          "Event rescheduled successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      return {
        status: false,
        data: errorData || null,
        message:
          errorData?.message ||
          errorData?.reason ||
          "An error occurred while rescheduling event",
      };
    }
  }

  async cancelEvent(event_slug, payload) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/cancel/event/${event_slug}`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.message ||
          response.data?.data?.reason ||
          "Event cancelled successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = "An error occurred while cancelling event";
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
      };
    }
  }

  async markAsClosed(event_slug) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/close/event/${event_slug}`,
        {},
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message: response.data?.data?.reason || "Event marked as closed",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error?.response?.data?.reason ||
          "An error occurred while closing event",
      };
    }
  }

  async getEventAttendees(event_slug) {
    try {
      const response = await axios.get(
        `${Config.API_BASE_URL}/admin/events/attendees/${event_slug}`,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data.data,
        message: response.data.data?.reason || "Attendees fetched successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error.response?.data?.reason ||
          "An error occurred while fetching attendees",
      };
    }
  }

  async cancelAttendeeTicket(event_slug, attendee_id, payload = {}) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/media/events/${event_slug}/attendees/${attendee_id}/cancel`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message: response.data?.data?.reason || "Ticket cancelled successfully",
      };
    } catch (error) {
      return {
        status: false,
        data: null,
        message:
          error?.response?.data?.reason ||
          "An error occurred while cancelling ticket",
      };
    }
  }

  // TRADE MISSION ACTIONS
  async rejectAttendeeRegistration(payload, event_slug) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/reject/event/${event_slug}`,
        payload,
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.data?.reason ||
          "Attendee registration rejected successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage =
        "An error occurred while rejecting attendee registration";
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
      };
    }
  }

  async approveAttendeeRegistration(event_slug) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/approved/event/${event_slug}`,
        {},
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.data?.reason ||
          "Attendee registration approved successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage =
        "An error occurred while approving attendee registration";
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
      };
    }
  }

  async markAttendeeAsPaid(event_slug) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/mark_as_paid/event/${event_slug}`,
        {},
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.data?.reason || "Attendee marked as paid successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      let errorMessage = "An error occurred while marking attendee as paid";
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      return {
        status: false,
        data: errorData || null,
        message: errorMessage,
      };
    }
  }

  // Generic attendee update endpoint (admin edit action)
  async updateAttendeeRegistration(attendee_slug, payload) {
    try {
      const response = await axios.post(
        `${Config.API_BASE_URL}/admin/update_attendee/event/${attendee_slug}`,
        payload || {},
        this.getHeaders()
      );
      return {
        status: true,
        data: response.data?.data,
        message:
          response.data?.data?.reason ||
          response.data?.message ||
          "Attendee updated successfully",
      };
    } catch (error) {
      const errorData = error?.response?.data;
      return {
        status: false,
        data: errorData || null,
        message:
          errorData?.reason ||
          errorData?.message ||
          "An error occurred while updating attendee",
        errors: errorData?.errors || null,
      };
    }
  }
}

const eventService = new EventService();
export default eventService;
